import { serviceCountryName } from './serviceRequestCountries.js';

export const S03_STAFF_CATEGORIES = [
    'C-Suite / Executives',
    'Key Personnel / Managers',
    'General Staff',
    'Contractors / Temporary Staff',
    'Field / Remote Workers',
] as const;

export const S03_CURRENT_MEASURES = [
    'Access control',
    'Security personnel',
    'CCTV / monitoring',
    'Travel approval / tracking',
    'Staff check-ins / duress',
    'Security training',
    'Incident reporting',
    'Emergency communications',
    'Lone-worker procedures',
    'None currently',
    'Unknown / consultant to verify',
] as const;

export type S03Intake = {
    spp_organization_name: string;
    spp_primary_address: string;
    spp_primary_country: string;
    spp_industry: string;
    spp_staff_categories: string[];
    spp_role_details: string;
    spp_headcount: string;
    spp_remote_locations: string;
    spp_current_measures: string[];
    spp_objectives_concerns: string;
};

const text = (value: unknown, maximum = 4000) => String(value ?? '').trim().slice(0, maximum);
const selected = (value: unknown, allowed: readonly string[]) => Array.isArray(value)
    ? [...new Set(value.map(item => text(item, 100)).filter(item => allowed.includes(item)))].slice(0, allowed.length)
    : [];

export function emptyS03Intake(): S03Intake {
    return {
        spp_organization_name: '', spp_primary_address: '', spp_primary_country: '', spp_industry: '',
        spp_staff_categories: [], spp_role_details: '', spp_headcount: '', spp_remote_locations: '',
        spp_current_measures: [], spp_objectives_concerns: '',
    };
}

export function prefillS03Intake(organization: any): S03Intake {
    const address = organization?.headquartersAddress || {};
    return {
        ...emptyS03Intake(),
        spp_organization_name: text(organization?.name || organization?.brandName, 240),
        spp_primary_address: [address.addressLine1, address.addressLine2, address.city, address.administrativeArea, address.postalCode]
            .map(part => text(part, 160)).filter(Boolean).join(', '),
        spp_primary_country: serviceCountryName(address.country || address.countryCode),
        spp_industry: text(organization?.industry, 160),
    };
}

export function cleanS03Intake(source: any): S03Intake {
    const input = source && typeof source === 'object' ? source : {};
    return {
        spp_organization_name: text(input.spp_organization_name, 240),
        spp_primary_address: text(input.spp_primary_address, 500),
        spp_primary_country: text(input.spp_primary_country, 120),
        spp_industry: text(input.spp_industry, 160),
        spp_staff_categories: selected(input.spp_staff_categories, S03_STAFF_CATEGORIES),
        spp_role_details: text(input.spp_role_details, 2000),
        spp_headcount: text(input.spp_headcount, 40),
        spp_remote_locations: text(input.spp_remote_locations, 3000),
        spp_current_measures: selected(input.spp_current_measures, S03_CURRENT_MEASURES),
        spp_objectives_concerns: text(input.spp_objectives_concerns, 4000),
    };
}

export function missingS03Intake(source: any): string[] {
    const intake = cleanS03Intake(source);
    const missing: string[] = [];
    if (!intake.spp_organization_name)
        missing.push('Organisation Name');
    if (!intake.spp_primary_address || !intake.spp_primary_country)
        missing.push('Primary Address and Country');
    if (!intake.spp_industry)
        missing.push('Industry / Sector');
    if (!intake.spp_staff_categories.length)
        missing.push('Staff Categories');
    if (!/^\d+$/.test(intake.spp_headcount) || Number(intake.spp_headcount) < 1)
        missing.push('Total Headcount');
    if (!intake.spp_current_measures.length)
        missing.push('Current Security Measures, None, or Unknown');
    if (intake.spp_current_measures.length > 1 && intake.spp_current_measures.some(item => item === 'None currently' || item === 'Unknown / consultant to verify'))
        missing.push('Non-conflicting Current Security Measures');
    if (!intake.spp_objectives_concerns)
        missing.push('Plan Objectives and Concerns');
    return missing;
}
